/**
 * NewsTeaser — home page shows the 3 most recent published CMS articles as
 * cards. The list is build-time-snapshotted from `cmsArticles` so this
 * component has zero runtime cost.
 *
 * Locale-aware: `cmsArticles` is built per-locale, and we fall back to
 * the previous locale (zh-Hant, en) when the user's locale has nothing.
 * `cover_media_uuid` resolves through `cmsMediaIndex`.
 */
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Container, Reveal } from '@/components/ui'
import ArticleCard from '@/components/cards/ArticleCard'
import { tCms } from '@/cms/tCms'
import { cmsArticles } from '@/content/cms/articles.generated'
import { cmsMediaIndex } from '@/content/cms/media.generated'

function titleForLocale(a: (typeof cmsArticles)[number], locale: 'zh-Hant' | 'zh-Hans' | 'en'): string {
  // The build-time index now preserves per-locale titles (see
  // CmsArticleIndexEntry). Pick the one for the active locale, then
  // fall back to zh-Hant → en — the same chain the server uses in
  // `pickLocale` (server/routes/public.ts:411-438) so the prerendered
  // HTML and the live CSR show the same cards.
  //
  // Every branch ends with `|| <collapsed title> || ''`. The `||` chain reads
  // as total-safety but is not: if the generated file predates the per-locale
  // fields (i.e. `cms:sync` never re-ran after the shape change), the earlier
  // links are all `undefined` and the tail `a.title` was the only thing keeping
  // this from returning `undefined` — which the caller then does `.length` on
  // → uncaught TypeError → the whole homepage white-screens, because
  // NewsTeaser sits under the hero with no error boundary above it. The
  // trailing `|| ''` is the belt-and-braces: even a generated file that lost
  // `title` too degrades to an empty string instead of a blank page.
  // Real fix for 2026-10-16 crash was re-running `npm run cms:sync`.
  if (locale === 'zh-Hant') return a.title_zhHant || a.title_en || a.title || ''
  if (locale === 'zh-Hans') return a.title_zhHans || a.title_zhHant || a.title_en || a.title || ''
  return a.title_en || a.title_zhHant || a.title || ''
}

function pickTop(locale: 'zh-Hant' | 'zh-Hans' | 'en', limit: number): typeof cmsArticles {
  // cmsArticles is already published_at DESC. Filter to ones that have
  // *some* title for this locale — empty means the operator hasn't filled
  // in that column yet. (The previous version checked the top-level
  // `title !== slug`, which was always the zh-Hant title; that falsely
  // excluded any article whose zh-Hant title happened to equal its slug,
  // e.g. `實用溫習策略`. See locale-filter bug note, 2026-10-16.)
  const withTitle = cmsArticles.filter((a) => titleForLocale(a, locale).length > 0)
  return withTitle.slice(0, limit)
}

export default function NewsTeaser({ settings }: { settings?: { limit?: number } } = {}) {
  // 3 is what this component shipped with; the CMS section row can override it.
  const limit = typeof settings?.limit === 'number' && settings.limit > 0 ? settings.limit : 3
  // Read the locale from react-i18next, not `document.documentElement.lang`.
  // The latter is written by a layout effect in App.tsx and therefore still
  // holds the *previous* language when this re-renders as part of the switch,
  // so the headlines came out in the old language until the next reload.
  const { i18n } = useTranslation()
  const locale = (i18n.language || 'zh-Hant') as 'zh-Hant' | 'zh-Hans' | 'en'

  const items = pickTop(locale, limit)

  return (
    <section className="py-section-y px-section-x bg-surface" aria-labelledby="news-title">
      <Container>
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:justify-between gap-3 sm:gap-4 mb-6 md:mb-7">
          <div>
            <span className="section-title__tagline inline-block mb-2 md:mb-3">
              {tCms('home', 'home.news.tag', locale)}
            </span>
            <h2
              id="news-title"
              className="section-title__title text-2xl sm:text-3xl md:text-4xl text-ink"
            >
              {tCms('home', 'home.news.title', locale)}
            </h2>
          </div>
          <Link
            to="/articles"
            className="inline-flex items-center self-start sm:self-auto gap-2 text-primary font-semibold hover:gap-3 transition-all text-sm md:text-base"
          >
            <span>{tCms('home', 'home.news.viewAll', locale)}</span>
            <Icon icon="lucide:arrow-right" />
          </Link>
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-ink-muted text-center py-12">尚無已發佈文章。</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-gap-cards">
            {items.map((a, idx) => (
              <Reveal key={a.slug} delay={idx * 0.1}>
                <ArticleCard
                  article={a}
                  cover={a.cover_media_uuid ? cmsMediaIndex[a.cover_media_uuid] : undefined}
                  locale={locale}
                  variant="full"
                  readMoreLabel={tCms('home', 'home.news.viewAll', locale)}
                />
              </Reveal>
            ))}
          </div>
        )}
      </Container>
    </section>
  )
}